import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUpIcon, AlertCircleIcon, CheckCircle2Icon, SparklesIcon, ClockIcon } from 'lucide-react';
import { Account } from '../types';
import { ActivityEvent } from '../lib/activityFeed';
interface AccountSummaryChipsProps {
  account: Account;
  activities: ActivityEvent[];
  onNavigate?: (view: string, metadata?: any) => void;
}
export function AccountSummaryChips({
  account,
  activities,
  onNavigate
}: AccountSummaryChipsProps) {
  // Calculate activity stats
  const recentActivityCount = activities.filter(a => Date.now() - new Date(a.timestamp).getTime() < 24 * 60 * 60 * 1000).length;
  const lastActivityTime = activities.length > 0 ? activities[activities.length - 1].timestamp : null;
  const getTimeAgo = (date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };
  const chips = [{
    id: 'activity',
    icon: TrendingUpIcon,
    label: 'Activity',
    value: `${recentActivityCount} today`,
    color: 'from-blue-500/20 to-blue-600/20 border-blue-500/30 text-blue-300',
    iconColor: 'text-blue-400'
  }];
  return <div className="relative px-6 py-4 border-b border-slate-800/80 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 backdrop-blur-xl" data-id="element-62">
      {/* Decorative gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" data-id="element-63" />
      <div className="relative space-y-3" data-id="element-64">
        {/* Quick Stats Chips */}
        <div className="flex items-center gap-2 flex-wrap" data-id="element-65">
          {chips.map((chip, index) => {
          const Icon = chip.icon;
          return <motion.div key={chip.id} initial={{
            opacity: 0,
            scale: 0.9,
            y: -10
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: index * 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className={`group relative flex items-center gap-2 px-3 py-2 rounded-xl border bg-gradient-to-br backdrop-blur-sm transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl overflow-hidden ${chip.color}`} data-id="element-66">
                {/* Hover gradient effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-67" />
                <div className={`w-5 h-5 rounded-lg bg-slate-900/40 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200`} data-id="element-68">
                  <Icon className={`w-3 h-3 ${chip.iconColor}`} data-id="element-69" />
                </div>
                <div className="flex items-center gap-2" data-id="element-70">
                  <span className="text-[10px] font-bold uppercase tracking-wider opacity-70" data-id="element-71">
                    {chip.label}
                  </span>
                  <span className="text-xs font-bold" data-id="element-72">{chip.value}</span>
                </div>
              </motion.div>;
        })}
        </div>
        {/* Last Activity Timestamp */}
        {lastActivityTime && <motion.div initial={{
        opacity: 0,
        y: -5
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.4
      }} className="flex items-center gap-2 text-xs text-slate-500" data-id="element-73">
            <ClockIcon className="w-3 h-3" data-id="element-74" />
            <span className="font-medium" data-id="element-75">
              Last activity: {getTimeAgo(new Date(lastActivityTime))}
            </span>
          </motion.div>}
      </div>
    </div>;
}